import { ref } from 'vue';
import { api, type FilterSet } from '../api.js';

/**
 * Ordering for the "Filters ▾" menu: favorited sets first (each group
 * alphabetical by name), then everything else alphabetical — per the binding
 * UX contract ("favorites first, then alphabetical"). Pulled out as a pure
 * function (mirroring `mergeSidebarOrder`) so it's unit-testable without
 * mounting the menu.
 */
export function sortFilterSets(sets: FilterSet[], favoriteIds: number[]): FilterSet[] {
  const favored = new Set(favoriteIds);
  const favorites = sets
    .filter((s) => favored.has(s.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const rest = sets.filter((s) => !favored.has(s.id)).sort((a, b) => a.name.localeCompare(b.name));
  return [...favorites, ...rest];
}

/**
 * Reactive wrapper around the org `filter_sets` list + the caller's
 * `favoriteFilterSets` user setting. Star toggles and deletes are optimistic
 * (rolled back on failure), mirroring `useUserSettings`'s `setSidebarOrder`.
 */
export function useFilterSets() {
  const sets = ref<FilterSet[]>([]);
  const favorites = ref<number[]>([]);
  const loading = ref(false);
  const error = ref(false);

  async function load(): Promise<void> {
    loading.value = true;
    try {
      const [list, favs] = await Promise.all([api.filterSets.list(), api.getFavoriteFilterSets()]);
      sets.value = list;
      favorites.value = favs;
      error.value = false;
    } catch {
      error.value = true;
    } finally {
      loading.value = false;
    }
  }

  /** Upsert-by-name (server semantics); refreshes the list on success. */
  async function save(name: string, query: string): Promise<{ id: number }> {
    const res = await api.filterSets.save(name, query);
    await load();
    return res;
  }

  async function remove(id: number): Promise<void> {
    const previous = sets.value;
    sets.value = sets.value.filter((s) => s.id !== id); // optimistic
    try {
      await api.filterSets.delete(id);
    } catch (e) {
      sets.value = previous; // roll back on failure
      throw e;
    }
  }

  async function toggleFavorite(id: number): Promise<void> {
    const previous = favorites.value;
    const next = previous.includes(id) ? previous.filter((v) => v !== id) : [...previous, id];
    favorites.value = next; // optimistic
    try {
      await api.putFavoriteFilterSets(next);
    } catch (e) {
      favorites.value = previous; // roll back on failure
      throw e;
    }
  }

  return { sets, favorites, loading, error, load, save, remove, toggleFavorite };
}
